import { CountryCode, DiscountType, TermUomType } from "@/gql/graphql";
import dayjs from "dayjs";
import z from "zod";

export const DATE_FORMAT = {
  DATE: "D MMM YYYY",
  DATE_TIME: "D MMM YYYY HH:mm",
  INVOICE_FILENAME_DATE: "YYYY-M-D",
};

export const DEFAULT_DATAGRID_PAGE_SETTINGS = {
  PAGINATED_SELECTOR: 3,
  DEFAULT_PAGE_SIZE: 20,
  PAGE_SIZE_OPTIONS: [5, 10, 20, 50],
};

export const DATE_PRESET_LOOKBACK_DAYS = {
  last7Days: 7,
  last30Days: 30,
  last90Days: 90,
  last1Year: 364,
} as const;

export const CREATE_CONTRACT = {
  CONTRACT_MIN_START_DATE: dayjs().add(2, "day"),
  CONTRACT_MAX_START_DATE: dayjs().add(30, "day"),
  DEFAULT_TERM_LENGTH: 1,
  DEFAULT_TERM_UOM: TermUomType.Year,
  DEFAULT_AUTO_RENEW: true,
  TERM_LENGTH_MONTHS_MAX: 11,
  TERM_LENGTH_YEARS_MAX: 5,
  TERM_LENGTH_MIN: 1,
  MAX_QUANTITY: 999,
  MIN_QUANTITY: 1,
  MAX_PO_NUMBER_LENGTH: 30,
  MAX_TRIAL_PER_SKU_PER_YEAR: 3,
};

export const AMEND_CONTRACT = {
  AMENDMENT_MIN_START_DATE: dayjs().add(2, "day"),
  AMENDMENT_MAX_START_DATE: dayjs().add(30, "day"),
  MAX_PO_NUMBER_LENGTH: CREATE_CONTRACT.MAX_PO_NUMBER_LENGTH,
};

export const SCHEDULE_OPTION = {
  NOW: "now",
  FUTURE: "future",
} as const;

export const LINKS = {
  TRIMBLE: "https://www.trimble.com",
  PARTNER_HUB: "https://partnerhub.trimble.com",
  SUPPORT: "https://www.trimble.com/en/support",
  LEGAL: "https://www.trimble.com/en/legal",
  TERMS_OF_USE:
    "https://www.trimble.com/en/legal/terms-and-conditions/terms-of-use",
  PRIVACY_CENTER:
    "https://www.trimble.com/en/our-commitment/responsible-business/data-privacy-and-security/data-privacy-center",
  PRIVACY_NOTICE:
    "https://www.trimble.com/en/our-commitment/responsible-business/data-privacy-and-security/data-privacy-center/privacy-notice",
  CALIFORNIA_NOTICE:
    "https://www.trimble.com/en/our-commitment/responsible-business/data-privacy-and-security/data-privacy-center/ca-residents-notice-at-collection",
  YOUR_PRIVACY_CHOICES_US:
    "https://www.trimble.com/en/our-commitment/responsible-business/data-privacy-and-security/data-privacy-center/your-privacy-choices",
  TERMS_AND_CONDITIONS_OF_SALE:
    "https://www.trimble.com/en/legal/terms-and-conditions/product-sale",
  CONTACT_US: "https://partnerhub.trimble.com/px/-/fs---contact-us-form",
};

export const IDLE_LOGOUT_TIME_MS = 1000 * 60 * 10;

export const MAX_CUSTOM_RANGE_DAYS = 365;

export const AUTH_REDIRECT_URI_STORAGE_KEY = "AUTH_REDIRECT_URI_STORAGE_KEY";

export const APP_PATHS = {
  SUPPORT_ADMIN: "support-admin",
} as const;

export const ROUTE_PARAMS = {
  ACCOUNT_ID: "accountId",
  ACCESS_DENIED: "accessDenied",
  /** No role/permissions for the dealer account (distinct from page permission denial). */
  ACCOUNT_ACCESS_DENIED: "accountAccessDenied",
  FILE_NOT_FOUND: "fileNotFound",
  CONTRACT_ID: "contractId",
  CUSTOMER_ID: "customerId",
  QUOTE_ID: "quoteId",
  TAB: "tab",
  BULK_SHIP_TO_CONTACT: "bulk-ship-to-contact",
  BULK_EMAIL_NOTICATIONS: "bulk-email-notifications",
  BULK_OPERATION_ID: "bulkOperationId",
} as const;

export const SEARCH_PARAMS = {
  SHIP_TO_CONTACT_ID: "shipToContactId",
  SHIP_TO_CONTACT_BULK_RESULT: "bulkResult",
  CUSTOMER_TABLE_RETURN: "customerTableReturn",
} as const;

export const TOAST_AUTO_HIDE_DURATION = 3000;
export const PROMO_CODE_MAX_LENGTH = 255;
export const SHIP_TO_CONTACT_BULK_LIMIT = 100;
export const MAX_BULK_EDIT_USERS = 50;

export const SHIP_TO_CONTACT_COLUMN_HEADER_HEIGHT_PX = 48;
export const SHIP_TO_CONTACT_ROW_HEIGHT_PX = 56;

export const AUDIT_LOG_PERFORMED_BY_FILTER_TRIMBLE_SUPPORT = "TRIMBLE_SUPPORT";

export const AUDIT_LOG_PERFORMED_BY_FILTER_SYSTEM = "SYSTEM";

/** Max data rows visible in a contract grid viewport before scrolling. */
export const CONTRACT_GRID_VISIBLE_ROWS = 7;

/** Matches `rowHeight` in `AppTheme` DataGrid defaults. */
export const CONTRACT_GRID_ROW_HEIGHT_PX = 76;

/** MUI DataGrid default column header height. */
export const CONTRACT_GRID_COLUMN_HEADER_HEIGHT_PX = 56;

/** Minimum height for customer summary grids while loading two rows. */
export const CUSTOMER_SUMMARY_TAB_GRID_MIN_HEIGHT_PX =
  CONTRACT_GRID_COLUMN_HEADER_HEIGHT_PX + CONTRACT_GRID_ROW_HEIGHT_PX * 2;

export const CONTRACTS_V2_MAX_PAGE_SIZE = 50;

/** Layout for Purchased Products toolbar filter dropdown. */
export const PURCHASED_PRODUCTS_FILTER_MENU_WIDTH =
  "min(480px, calc(100vw - 24px))";
export const PURCHASED_PRODUCTS_FILTER_MENU_MAX_HEIGHT_PX = 420;
export const PURCHASED_PRODUCTS_FILTER_VIEWPORT_PADDING_PX = 16;
export const PURCHASED_PRODUCTS_FILTER_MENU_MIN_HEIGHT_PX = 180;
export const PURCHASED_PRODUCTS_FILTER_MENU_BORDER_RADIUS = "8px";
export const PURCHASED_PRODUCTS_FILTER_LIST_MAX_HEIGHT_PX = 320;

export const CUSTOMER_REQUEST = {
  MAX_COMMENT_LENGTH: 500,
};

export { PendingRequestType } from "@/components/ContractSummary2/types";

export const EMAIL_VALIDATION_REGEX = z.regexes.html5Email;

export const RENEWS_IN = 7;
export const CONTRACT_NEAR_RENEWAL_DAYS = 8;
export const DISCOUNT_ORDER = [
  DiscountType.Volume,
  DiscountType.Standard,
  DiscountType.Promo,
  DiscountType.Kpi,
  DiscountType.Discretionary,
];

export const RENEW_CONTRACT_TERM = {
  ANNUAL: "YEAR",
  MONTHLY: "MONTH",
};

export const REASON_TEXT_AREA = {
  MIN_LENGTH: 1,
  MAX_LENGTH: 500,
};

export const CURRENCIES = {
  AVAILABLE_IDS: ["USD", "EUR"] as const,
  US_ID: "USD" as const,
  EUR_ID: "EUR" as const,
} as const;

export const RESTRICTED_COUNTRY_CODES = [
  CountryCode.Cn,
  CountryCode.Ve,
  CountryCode.Sd,
  CountryCode.Sy,
];

export const ERROR_CODES = {
  AMENDMENT_NOT_APPLICABLE: "DXP_1213",
} as const;
export const DISCRETIONARY_DISCOUNT_MAX_VALUE = 100;
export const DISCRETIONARY_DISCOUNT_MIN_VALUE = 0.5;
export const DISCRETIONARY_DISCOUNT_INPUT_MIN_VALUE = 0;
export const MAX_GLOBAL_SEARCH_OPTIONS = 5;
